import { Box, Divider, Typography } from "@mui/material";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { ObjectsEnum } from "..";
import SolidGradientColorPicker from "../../../../../../../components/common/SolidGradientColorPicker/SolidGradientColorPicker";
import { useAppStore } from "../../../../../../../hooks/useStores";
import useDesignEditorUtils, { FeatureType, Property } from "../../../../../../CustomUtils/UseDesignEditor";
import MainTabHeader from "../../../../../../components/common/MainTabHeader/MainTabHeader";
import { setFabricElementGradient } from "../../../../../../core/utils/fabric";
import {
	getFillColor,
	isGradient,
	parseGradientString,
	updateGradientStopColor,
} from "../../../../../../core/utils/gradient_color_utils";
import UseBrandConfigs from "../../../../../../hooks/useBrandConfigs";
import { useEditor } from "../../../../../../hooks/useEditor";
import { LayerType } from "../../../../../../types";
import SelectedBrandColors from "../Common/SelectedBrandColors/SelectedBrandColors";
import styles from "./CanvasColor.module.scss";
export type Iprops = {};

const CanvasColor = ({}: Iprops) => {
	const { t } = useTranslation("translation");
	const { t: t1 } = useTranslation("translation", { keyPrefix: "editor.tabs" });
	const editor = useEditor();
	const { campaignStore, brandsDefinitionStore } = useAppStore();
	const { EditAllAds, getRgbAndOpacityFromRgba, updateColorOpacity } = useDesignEditorUtils();
	const { modifyActiveObjectProperty } = UseBrandConfigs();
	const [color, setColor] = useState(() => {
		if (editor?.frame?.background?.fill) {
			const fill = editor.frame.background.fill;
			const fillColor = getFillColor(fill);
			if (typeof fill === "string") {
				return updateColorOpacity(fillColor, editor.frame.background.opacity);
			}
			return fillColor;
		}
		return "#fff";
	});
	const [selectedStopIndex, setSelectedStopIndex] = useState(0);
	const [initialFrame, setInitialFrame] = useState(undefined);
	useEffect(() => {
		let watcher = async () => {
			const initialFrame = editor?.objects.findOneById(ObjectsEnum.InitialFrame);
			setInitialFrame(initialFrame);
			const currentFill = editor?.frame?.background?.fill;

			if (currentFill) {
				const fillColor = getFillColor(currentFill);
				if (typeof currentFill === "string") {
					const updatedColor = updateColorOpacity(fillColor, editor.frame.background.opacity);
					setColor(updatedColor);
				} else {
					// It's a gradient object
					setColor(fillColor);
				}
			} else {
				setColor("#fff");
			}
		};
		if (editor) {
			editor.on("canvas:updated", watcher);
		}
		return () => {
			if (editor) {
				editor.off("canvas:updated", watcher);
			}
		};
	}, [editor]);

	useEffect(() => {
		const initialFrame = editor?.objects.findOneById(ObjectsEnum.InitialFrame);
		setInitialFrame(initialFrame);
	}, []);

	const handleStopSelection = (index: number) => {
		setSelectedStopIndex(index);
	};

	const getFinalColor = (newColor: string, isBrandColor: boolean): string => {
		const currentFillIsGradient = isGradient(color);
		const newColorIsGradient = isGradient(newColor);

		// If brand color is selected and current fill is a gradient, update the selected stop
		if (isBrandColor && currentFillIsGradient && !newColorIsGradient) {
			const updatedGradient = updateGradientStopColor(color, newColor, selectedStopIndex);
			return updatedGradient || newColor;
		}

		return newColor;
	};

	const applyGradientToCanvas = (finalColor: string, brandColor: boolean) => {
		const gradientData = parseGradientString(finalColor);
		if (!gradientData || !editor) return;

		const gradient = setFabricElementGradient(editor.frame.background, gradientData.angle, gradientData.colorStops);
		if (gradient) editor.frame.background.set({ fill: gradient, opacity: 1 });
		if (!brandColor) {
			editor.objects.update({ opacity: 1, brandId: undefined }, undefined, false);
		}
		editor.canvas.requestRenderAll();
	};

	const applySolidColorToCanvas = (finalColor: string, brandColor: boolean) => {
		if (!editor) return;

		const finalResult = getRgbAndOpacityFromRgba(finalColor);
		editor.frame.setBackgroundColor(finalResult?.rgb ?? finalColor);
		if (!brandColor) {
			editor.frame.background.set({ opacity: finalResult?.opacity ?? 1 });
			editor.objects.update({ opacity: finalResult?.opacity ?? 1, brandId: undefined }, undefined, false);
		}
	};

	const renderGradientStopSelector = () => {
		if (!isGradient(color)) return null;

		const gradientData = parseGradientString(color);
		if (!gradientData || gradientData.colorStops.length <= 1) return null;

		return (
			<Box className={styles.gradientStopSelector}>
				<Typography className={styles.label}>{t1("selectGradientStop")}</Typography>
				<Box className={styles.stopsContainer}>
					{gradientData.colorStops.map((stop, index) => (
						<Box
							key={index}
							onClick={() => handleStopSelection(index)}
							className={`${styles.stopBox} ${selectedStopIndex === index ? styles.selected : ""}`}
							style={{ backgroundColor: stop.color }}
							title={`${t1("stop", { ns: "ads_generation" })} ${index + 1} (${Math.round(
								stop.offset * 100,
							)}%)`}
						/>
					))}
				</Box>
			</Box>
		);
	};

	const updateBackgroundColor = (newColor: string, brandColor: boolean = false) => {
		// Determine final color (brand color may update gradient stop)
		const finalColor = getFinalColor(newColor, brandColor);
		const isFinalGradient = isGradient(finalColor);

		setColor(finalColor);
		campaignStore.canvasColorPicker = finalColor;

		if (editor) {
			// Apply color based on type (gradient or solid)
			if (isFinalGradient) {
				applyGradientToCanvas(finalColor, brandColor);
			} else {
				applySolidColorToCanvas(finalColor, brandColor);
			}

			// Save and sync across all ads
			const colorForEditAllAds = brandColor
				? updateColorOpacity(finalColor, editor.frame.background.opacity)
				: null;

			if (!campaignStore.isEditingAllAds) editor.history.save();
			EditAllAds(LayerType.BACKGROUND, "", FeatureType.COLOR, colorForEditAllAds ?? finalColor);
		}
	};

	return (
		<>
			<Box className={styles.container}>
				<MainTabHeader title={t(`editor.panels.panelsList.fillColor`)} />
				<Box className={`${styles.tabs} ${styles.fullHeight}`}>
					<Box className={styles.ColorFillContent}>
						<Box className={styles.colorPickerContainer}>
							<SolidGradientColorPicker
								color={color}
								onChange={(newColor: string) => {
									updateBackgroundColor(newColor);
									modifyActiveObjectProperty(initialFrame, Property.COLOR_NUMBER, undefined);
								}}
								hideColorTypeBtns={false}
							/>
						</Box>

						<Divider className={styles.mainDivider} />
						{brandsDefinitionStore.globalSelectedBrand?.color_pallete?.colors?.some(Boolean) &&
							renderGradientStopSelector()}
						<SelectedBrandColors
							activeObject={initialFrame}
							updateColor={(brandColor: string) => {
								updateBackgroundColor(brandColor, true);
							}}
							property={Property.COLOR_NUMBER}
						/>
					</Box>
				</Box>
			</Box>
		</>
	);
};

export default CanvasColor;
